import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Zap, ShieldCheck, MonitorSmartphone } from "lucide-react";
import { useI18n } from "../../i18n/I18nProvider";

export default function Hero() {
  const { t } = useI18n();
  return (
    <section
      data-testid="hero-section"
      className="relative overflow-hidden pt-12 md:pt-20 pb-20 md:pb-28"
    >
      <div className="absolute inset-0 technical-grid pointer-events-none" />
      <div className="container-oj relative">
        <div className="grid grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="col-span-12 lg:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-white/70 backdrop-blur px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-700"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-oj-kiosk animate-pulse" />
              {t("hero.eyebrow")}
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-balance mt-6 text-[44px] leading-[0.95] sm:text-6xl lg:text-[88px] font-black tracking-tighter text-oj-ink"
            >
              {t("hero.title_a")}
              <span className="relative inline-block ml-3">
                <span className="bg-oj-pos text-white px-3 rounded-md">{t("hero.title_restaurants")}</span>
              </span>
              <br className="hidden sm:block" />
              {t("hero.title_and")}{" "}
              <span className="relative inline-block">
                <span className="bg-oj-kiosk text-white px-3 rounded-md">{t("hero.title_retail")}</span>
              </span>
              .
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-6 max-w-xl text-lg sm:text-xl text-zinc-600 leading-relaxed pretty"
            >
              {t("hero.sub")}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/#demo"
                data-testid="hero-primary-cta"
                className="inline-flex items-center gap-1.5 bg-oj-brand text-oj-deep font-semibold px-6 py-4 rounded-md hover:bg-[#6FBDB9] transition-colors"
              >
                {t("hero.cta_demo")} <ArrowUpRight className="size-5" />
              </Link>
              <Link
                to="/solutions/kiosk"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-1.5 border border-zinc-300 bg-white text-oj-ink font-semibold px-6 py-4 rounded-md hover:border-zinc-900 transition-colors"
              >
                {t("hero.cta_kiosks")}
              </Link>
            </div>

            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-xl border-t border-zinc-200 pt-6">
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-500">{t("hero.stat_live")}</dt>
                <dd className="mt-1 font-display text-2xl sm:text-3xl font-black text-oj-ink">{t("hero.live_days")}</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-500">{t("hero.stat_uplift")}</dt>
                <dd className="mt-1 font-display text-2xl sm:text-3xl font-black text-oj-ink">+22%</dd>
              </div>
              <div>
                <dt className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-500">{t("hero.stat_uptime")}</dt>
                <dd className="mt-1 font-display text-2xl sm:text-3xl font-black text-oj-ink">99.99%</dd>
              </div>
            </dl>
          </div>

          <div className="col-span-12 lg:col-span-5">
            <HeroVisual />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroVisual() {
  return (
    <div className="relative" data-testid="hero-visual">
      <div className="absolute -inset-4 bg-gradient-to-tr from-oj-kiosk/10 via-transparent to-oj-pos/10 blur-2xl" />
      <div className="relative rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
        <img
          src="/images/hero-pos.jpg"
          alt="Restaurant team using O'javana POS"
          className="w-full h-[420px] object-cover"
          loading="eager"
        />
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[11px] font-mono">
          <div className="flex items-center gap-1.5 bg-white/90 backdrop-blur px-2 py-1 rounded-md">
            <span className="h-1.5 w-1.5 rounded-full bg-oj-kiosk" /> live · station 04
          </div>
          <div className="bg-white/90 backdrop-blur px-2 py-1 rounded-md">14:42</div>
        </div>
      </div>

      <div className="absolute -bottom-6 -left-6 sm:-left-10 w-[78%] bg-white border border-zinc-200 rounded-2xl shadow-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 rounded-md bg-oj-pos/10 grid place-items-center">
              <Zap className="size-4 text-oj-pos" />
            </span>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Order #3419</div>
              <div className="font-display font-bold text-oj-ink">CHF 42.80 · paid</div>
            </div>
          </div>
          <div className="text-[11px] text-oj-kiosk font-semibold">+1 tip CHF 4.30</div>
        </div>
        <div className="mt-3 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className="h-full w-[68%] bg-oj-kiosk" />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
          <span className="font-mono">kitchen 0:42</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="size-3" /> EMV</span>
          <span className="inline-flex items-center gap-1"><MonitorSmartphone className="size-3" /> POS-04</span>
        </div>
      </div>
    </div>
  );
}
